/**
 * Program Setup Wizard
 * Comprehensive wizard for sponsors to create and configure programs
 * Integrates: Program creation, matching criteria, CBO discovery, invitations, terms
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { getWeaveinApiUrl } from '@/src/config';
import { programsAPI } from '@/services/api/programs';
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronLeft,
  Target,
  Users,
  MapPin,
  DollarSign,
  Award,
  Sparkles,
  Building2,
  Network,
  FileText,
  Send,
  AlertCircle,
  ExternalLink,
  Search,
  Plus,
  X
} from 'lucide-react';
import ROIModeler from './ROIModeler';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface ProgramSetupWizardProps {
  sponsorId: number;
  editProgramId?: number; // If provided, wizard loads existing program for editing
  onComplete?: (programId: number) => void;
  onCancel?: () => void;
}

interface ProgramData {
  name: string;
  program_type: string;
  description: string;
  eligibility_criteria: string;
  target_population: string;
  geographic_scope: string;
}

interface MatchingCriteria {
  required_services: string[];
  target_sdoh_areas: string[];
  target_clinical_gaps: string[];
  target_zip_codes: string[];
  target_counties: string[];
  min_success_rate: number;
  min_match_score: number;
  require_manual_review: boolean;
  max_cbos_total: number;
}

interface TermsData {
  outcome_based_payment: boolean;
  enrollment_payment: number;
  milestone_payments: { name: string; amount: number }[];
  completion_bonus: number;
}

interface ROIConfigData {
  enrollment_target: number;
  total_budget: number;
  program_duration_months: number;
  avg_pmpm_rate: number;
  baseline_pmpm_cost: number;
  target_pmpm_cost: number;
  baseline_ed_visits_per_1000: number;
  target_ed_visits_per_1000: number;
  avg_ed_visit_cost: number;
  baseline_hospital_admits_per_1000: number;
  target_hospital_admits_per_1000: number;
  avg_hospital_admit_cost: number;
  target_retention_rate_6mo: number;
  target_retention_rate_12mo: number;
  admin_overhead_percentage: number;
}

interface ROIScenarioData {
  id: string;
  name: string;
  description: string;
  config: ROIConfigData;
  projections?: {
    projected_annual_revenue: number;
    projected_annual_savings: number;
    projected_roi_percentage: number;
    projected_roi_multiple: number;
    cost_per_enrollment: number;
    total_program_cost: number;
    net_benefit: number;
    payback_months: number;
    break_even_enrollments: number;
  };
  confidence: number;
  source: 'manual' | 'ai' | 'benchmark' | 'historical';
}

interface ConnectedDataSource {
  id: string;
  name: string;
  type: string;
  lastSync?: string;
}

interface MilestoneConfigItem {
  milestone_id: number;
  milestone_name: string;
  milestone_type: string;
  is_required: boolean;
  target_quantity: number;
  cbo_payout_amount: number;
  member_payout_amount: number;
  points_awarded: number;
}

// Complete wizard draft state for auto-save
interface WizardDraftState {
  currentStep: number;
  programData: ProgramData;
  matchingCriteria: MatchingCriteria;
  matchedCBOs: any[];
  selectedCBOs: number[];
  termsData: TermsData;
  createdProgramId: number | null;
  roiConfig: ROIConfigData;
  roiScenarios: ROIScenarioData[];
  connectedDataSources: ConnectedDataSource[];
  selectedScenarioId: string | null;
  milestoneConfigs: MilestoneConfigItem[];
  isEditMode: boolean;
  savedAt: string;
}

// LocalStorage key for wizard drafts
const WIZARD_DRAFT_KEY = 'program_wizard_draft';

interface AvailableMilestone {
  id: number;
  activity_type: string;
  milestone_name: string;
  milestone_description: string | null;
  points_awarded: number;
  monetary_value: number;
  is_repeatable: boolean;
  is_active: boolean;
}

const WIZARD_STEPS = [
  {
    id: 1,
    name: 'Program Info',
    icon: FileText,
    description: 'Basic program details',
    guidance: 'Define your program name, type, and description. This information will be visible to CBOs when they discover your program.'
  },
  {
    id: 2,
    name: 'Parameters',
    icon: Target,
    description: 'Eligibility & targeting',
    guidance: 'Specify who can participate in this program and what geographic areas it covers.'
  },
  {
    id: 3,
    name: 'Matching',
    icon: Sparkles,
    description: 'CBO discovery criteria',
    guidance: 'Set the criteria to automatically find CBOs that match your program needs based on services, specializations, and performance.'
  },
  {
    id: 4,
    name: 'Preview CBOs',
    icon: Users,
    description: 'Review matched CBOs',
    guidance: 'Review the CBOs that match your criteria and select which ones to invite to participate in your program.'
  },
  {
    id: 5,
    name: 'Terms',
    icon: DollarSign,
    description: 'Payment structure',
    guidance: 'Configure CBO payment terms including enrollment incentives and milestone bonuses that you will pay to participating CBOs.'
  },
  {
    id: 6,
    name: 'Milestones',
    icon: Award,
    description: 'Activity milestones',
    guidance: 'Select which milestone activities to track for this program. Define CBO and member payouts for each completed milestone.'
  },
  {
    id: 7,
    name: 'ROI Model',
    icon: Target,
    description: 'ROI projections',
    guidance: 'Configure your return on investment model with enrollment targets, budget, and cost savings projections to measure program success.'
  },
  {
    id: 8,
    name: 'Review',
    icon: CheckCircle2,
    description: 'Launch program',
    guidance: 'Review all your program settings and launch when ready. You can still edit the program after launch.'
  }
];

// Fallback types used while API types are loading
const DEFAULT_PROGRAM_TYPES = [
  { value: 'CalAIM_ECM', label: 'CalAIM Enhanced Care Management', description: 'Coordinated care for complex health needs' },
  { value: 'CalAIM_CS', label: 'CalAIM Community Supports', description: 'Housing, food, and transportation services' },
  { value: 'Organizing', label: 'Organizing', description: 'Community organizing and advocacy programs' },
  { value: 'Other', label: 'Other', description: 'Custom program type' },
];

const SERVICE_OPTIONS = [
  // Core Care Management
  'case_management', 'care_coordination', 'care_transitions', 'discharge_planning',

  // Clinical Services
  'primary_care', 'behavioral_health', 'mental_health_counseling', 'psychiatric_services',
  'substance_abuse_treatment', 'addiction_counseling', 'medication_management',
  'chronic_disease_management', 'diabetes_management', 'hypertension_management',
  'asthma_management', 'heart_disease_management',

  // Preventive & Wellness
  'health_education', 'wellness_coaching', 'nutrition_counseling', 'physical_therapy',
  'occupational_therapy', 'preventive_screenings', 'immunizations',

  // SDOH Services
  'housing_navigation', 'housing_assistance', 'homeless_services',
  'food_assistance', 'food_pantry', 'meal_delivery',
  'transportation', 'transportation_coordination',
  'utility_assistance', 'financial_counseling', 'benefits_enrollment',
  'employment_assistance', 'job_training', 'legal_services',

  // Support Services
  'peer_support', 'support_groups', 'respite_care', 'caregiver_support',
  'home_health', 'personal_care', 'durable_medical_equipment',

  // Specialized Programs
  'maternal_health', 'pediatric_care', 'geriatric_care', 'palliative_care',
  'hospice_care', 'wound_care', 'dialysis_support',

  // Language & Cultural
  'interpretation_services', 'translation_services', 'cultural_competency_services'
];

const SDOH_OPTIONS = [
  'food_insecurity', 'housing_insecurity', 'homelessness',
  'transportation', 'utilities', 'financial_strain',
  'behavioral_health', 'mental_health', 'substance_abuse',
  'social_isolation', 'domestic_violence', 'elder_abuse',
  'unemployment', 'education', 'legal_issues', 'immigration_status',
  'disability_support', 'chronic_pain', 'health_literacy'
];

export const ProgramSetupWizard: React.FC<ProgramSetupWizardProps> = ({
  sponsorId,
  editProgramId,
  onComplete,
  onCancel
}) => {
  const { t } = useAppTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftPrograms, setDraftPrograms] = useState<any[]>([]);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Program types fetched from API
  const [programTypes, setProgramTypes] = useState<{ value: string; label: string; description?: string }[]>(DEFAULT_PROGRAM_TYPES);

  useEffect(() => {
    const fetchProgramTypes = async () => {
      try {
        const types = await programsAPI.getProgramTypes();
        setProgramTypes(types);
      } catch (err) {
        console.error('Error fetching program types:', err);
      }
    };
    fetchProgramTypes();
  }, []);

  // Program data
  const [programData, setProgramData] = useState<ProgramData>({
    name: '',
    program_type: '',
    description: '',
    eligibility_criteria: '',
    target_population: '',
    geographic_scope: ''
  });

  // Matching criteria
  const [matchingCriteria, setMatchingCriteria] = useState<MatchingCriteria>({
    required_services: [],
    target_sdoh_areas: [],
    target_clinical_gaps: [],
    target_zip_codes: [],
    target_counties: [],
    min_success_rate: 0.70,
    min_match_score: 70,
    require_manual_review: false,
    max_cbos_total: 100
  });

  // Matched CBOs
  const [matchedCBOs, setMatchedCBOs] = useState<any[]>([]);
  const [selectedCBOs, setSelectedCBOs] = useState<number[]>([]);

  // Terms data
  const [termsData, setTermsData] = useState<TermsData>({
    outcome_based_payment: true,
    enrollment_payment: 50,
    milestone_payments: [],
    completion_bonus: 100
  });

  // Created program ID (after step 1)
  const [createdProgramId, setCreatedProgramId] = useState<number | null>(null);

  // ROI Configuration
  const [roiConfig, setRoiConfig] = useState<ROIConfigData>({
    enrollment_target: 1000,
    total_budget: 500000,
    program_duration_months: 12,
    avg_pmpm_rate: 500,
    baseline_pmpm_cost: 800,
    target_pmpm_cost: 600,
    baseline_ed_visits_per_1000: 450,
    target_ed_visits_per_1000: 320,
    avg_ed_visit_cost: 2500,
    baseline_hospital_admits_per_1000: 180,
    target_hospital_admits_per_1000: 140,
    avg_hospital_admit_cost: 18000,
    target_retention_rate_6mo: 85,
    target_retention_rate_12mo: 75,
    admin_overhead_percentage: 15
  });

  // ROI Scenarios and Data Sources (for persistence)
  const [roiScenarios, setRoiScenarios] = useState<ROIScenarioData[]>([]);
  const [connectedDataSources, setConnectedDataSources] = useState<ConnectedDataSource[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);

  // Milestone Configuration
  const [milestoneConfigs, setMilestoneConfigs] = useState<MilestoneConfigItem[]>([]);
  const [availableMilestones, setAvailableMilestones] = useState<AvailableMilestone[]>([]);

  // Manual CBO Addition State
  const [cboSelectionTab, setCboSelectionTab] = useState<'matched' | 'manual'>('matched');
  const [cboSearchQuery, setCboSearchQuery] = useState('');
  const [cboSearchResults, setCboSearchResults] = useState<any[]>([]);
  const [searchingCbos, setSearchingCbos] = useState(false);
  const [showNewCboForm, setShowNewCboForm] = useState(false);
  const [newCboData, setNewCboData] = useState({
    name: '',
    city: '',
    state: '',
    county: '',
    zip_code: '',
    phone: '',
    email: '',
    contact_name: '',
    contact_email: '',
    notes: ''
  });

  // Auto-save wizard state to localStorage
  const saveWizardDraft = () => {
    // Use different keys for new vs editing programs
    const draftKey = editProgramId
      ? `program_wizard_edit_${editProgramId}`
      : WIZARD_DRAFT_KEY;

    const draftState: WizardDraftState = {
      currentStep,
      programData,
      matchingCriteria,
      matchedCBOs,
      selectedCBOs,
      termsData,
      createdProgramId,
      roiConfig,
      roiScenarios,
      connectedDataSources,
      selectedScenarioId,
      milestoneConfigs,
      isEditMode,
      savedAt: new Date().toISOString()
    };

    try {
      localStorage.setItem(draftKey, JSON.stringify(draftState));
      console.log(`✅ Auto-saved wizard draft at step ${currentStep} (key: ${draftKey})`);
    } catch (err) {
      console.error('Failed to save wizard draft:', err);
    }
  };

  // Load wizard draft from localStorage
  const loadWizardDraft = (): WizardDraftState | null => {
    try {
      // Check for edit draft first if editing
      if (editProgramId) {
        const editDraft = localStorage.getItem(`program_wizard_edit_${editProgramId}`);
        if (editDraft) {
          console.log('📂 Loaded edit draft for program', editProgramId);
          return JSON.parse(editDraft);
        }
      }
      // Otherwise check for new program draft
      const saved = localStorage.getItem(WIZARD_DRAFT_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Failed to load wizard draft:', err);
    }
    return null;
  };

  // Clear wizard draft from localStorage
  const clearWizardDraft = () => {
    try {
      if (editProgramId) {
        localStorage.removeItem(`program_wizard_edit_${editProgramId}`);
        console.log('🗑️ Cleared edit draft for program', editProgramId);
      } else {
        localStorage.removeItem(WIZARD_DRAFT_KEY);
        console.log('🗑️ Cleared wizard draft');
      }
    } catch (err) {
      console.error('Failed to clear wizard draft:', err);
    }
  };

  // Resume from saved draft
  const resumeDraft = (draft: WizardDraftState) => {
    setCurrentStep(draft.currentStep);
    setProgramData(draft.programData);
    setMatchingCriteria(draft.matchingCriteria);
    setMatchedCBOs(draft.matchedCBOs || []);
    setSelectedCBOs(draft.selectedCBOs || []);
    setTermsData(draft.termsData);
    setCreatedProgramId(draft.createdProgramId);
    setRoiConfig(draft.roiConfig);
    setRoiScenarios(draft.roiScenarios || []);
    setConnectedDataSources(draft.connectedDataSources || []);
    setSelectedScenarioId(draft.selectedScenarioId);
    setMilestoneConfigs(draft.milestoneConfigs || []);
    setIsEditMode(draft.isEditMode || false);
    setShowDraftPrompt(false);
    console.log('✅ Resumed wizard from draft at step', draft.currentStep);
  };

  // Discard saved draft and start fresh
  const discardDraft = () => {
    clearWizardDraft();
    setShowDraftPrompt(false);
  };

  // Check for existing draft on mount
  useEffect(() => {
    // Don't show draft prompt if editing existing program
    if (editProgramId) return;

    const savedDraft = loadWizardDraft();
    if (savedDraft && savedDraft.programData.name) {
      setDraftPrograms([savedDraft]);
      setShowDraftPrompt(true);
    }
  }, [editProgramId]);

  // Auto-save on state changes (debounced effect)
  useEffect(() => {
    // Don't auto-save if showing draft prompt or editing existing program
    if (showDraftPrompt || editProgramId) return;

    // Only save if there's meaningful data (user has entered something)
    const hasData = programData.name || programData.description || currentStep > 1;
    if (!hasData) return;

    // Save after a short delay to batch rapid changes
    const timeoutId = setTimeout(() => {
      saveWizardDraft();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    currentStep,
    programData,
    matchingCriteria,
    matchedCBOs,
    selectedCBOs,
    termsData,
    createdProgramId,
    roiConfig,
    roiScenarios,
    connectedDataSources,
    selectedScenarioId,
    milestoneConfigs,
    isEditMode,
    showDraftPrompt,
    editProgramId
  ]);

  // Load existing program data when editing
  useEffect(() => {
    if (editProgramId) {
      loadExistingProgram(editProgramId);
    }
  }, [editProgramId]);

  const loadExistingProgram = async (programId: number) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${programId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Program not found. It may have been deleted.');
        }
        throw new Error(`Failed to load program (${response.status})`);
      }

      const program = await response.json();

      // Set edit mode
      setIsEditMode(true);
      setCreatedProgramId(programId);

      // Populate program data from existing program
      const eligCriteria = program.eligibility_criteria || {};
      setProgramData({
        name: program.name || '',
        program_type: program.program_type || '',
        description: program.description || '',
        eligibility_criteria: eligCriteria.criteria || '',
        target_population: eligCriteria.target_population || '',
        geographic_scope: eligCriteria.geographic_scope || ''
      });

      // Load matching criteria for step 2
      try {
        const criteriaResponse = await fetch(
          getWeaveinApiUrl(`/api/program-matching-criteria/${programId}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': cboId || ''
            }
          }
        );

        if (criteriaResponse.ok) {
          const criteria = await criteriaResponse.json();
          setMatchingCriteria({
            required_services: criteria.required_services || [],
            target_sdoh_areas: criteria.target_sdoh_areas || [],
            target_clinical_gaps: criteria.target_clinical_gaps || [],
            target_zip_codes: criteria.target_zip_codes || [],
            target_counties: criteria.target_counties || [],
            min_success_rate: criteria.min_success_rate || 70,
            min_match_score: criteria.min_match_score || 70,
            require_manual_review: criteria.require_manual_review ?? true,
            max_cbos_total: criteria.max_cbos_total || 20
          });
          console.log('✅ Loaded matching criteria for step 2');
        } else if (criteriaResponse.status !== 404) {
          console.warn('Failed to load matching criteria:', criteriaResponse.status);
        }
      } catch (criteriaErr) {
        console.warn('Error loading matching criteria:', criteriaErr);
      }

      console.log(`✅ Loaded draft program for editing: ${program.name}`);
    } catch (err) {
      console.error('Error loading program:', err);
      setError(err instanceof Error ? err.message : 'Failed to load program');
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = (currentStep / WIZARD_STEPS.length) * 100;

  const handleNext = async () => {
    if (currentStep === 1) {
      if (isEditMode) {
        await updateProgram();
      } else {
        await createProgram();
      }
    } else if (currentStep === 2) {
      await saveMatchingCriteria();
    } else if (currentStep === 3) {
      await fetchMatchedCBOs();
    } else if (currentStep === 4) {
      // Move to terms
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 5) {
      await saveTerms();
    } else if (currentStep === 6) {
      // Milestone config - save and move to ROI
      await saveMilestoneConfig();
    } else if (currentStep === 7) {
      // ROI config - save and move to review
      await saveROIConfig();
    } else if (currentStep === 8) {
      await launchProgram();
    }
  };

  const fetchAvailableMilestones = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        getWeaveinApiUrl('/api/member-activity-milestones/'),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableMilestones(data.filter((m: AvailableMilestone) => m.is_active));
      }
    } catch (err) {
      console.error('Error fetching milestones:', err);
    }
  };

  // Search for existing CBOs
  const searchCBOs = async (query: string) => {
    if (query.length < 2) {
      setCboSearchResults([]);
      return;
    }

    try {
      setSearchingCbos(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl(`/api/organizations/search?q=${encodeURIComponent(query)}&limit=20`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (response.ok) {
        const results = await response.json();
        // Filter out already selected CBOs
        const filtered = results.filter((org: any) => !selectedCBOs.includes(org.id));
        setCboSearchResults(filtered);
      }
    } catch (err) {
      console.error('Error searching CBOs:', err);
    } finally {
      setSearchingCbos(false);
    }
  };

  // Create a new CBO as opportunity
  const createOpportunityCBO = async () => {
    if (!newCboData.name) {
      setError('Organization name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl('/api/organizations/opportunity'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify(newCboData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create organization');
      }

      const result = await response.json();
      const newOrg = result.organization;

      // Add to selected CBOs and matched CBOs list
      setSelectedCBOs([...selectedCBOs, newOrg.id]);
      setMatchedCBOs([...matchedCBOs, {
        cbo_id: newOrg.id,
        cbo_name: newOrg.name,
        match_score: 0,
        match_quality: 'Manual',
        customer_status: 'OPPORTUNITY'
      }]);

      // Reset form
      setNewCboData({
        name: '',
        city: '',
        state: '',
        county: '',
        zip_code: '',
        phone: '',
        email: '',
        contact_name: '',
        contact_email: '',
        notes: ''
      });
      setShowNewCboForm(false);

      console.log(`✅ Created new opportunity CBO: ${newOrg.name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  // Add CBO from search results
  const addCboFromSearch = (org: any) => {
    if (!selectedCBOs.includes(org.id)) {
      setSelectedCBOs([...selectedCBOs, org.id]);
      setMatchedCBOs([...matchedCBOs, {
        cbo_id: org.id,
        cbo_name: org.name,
        match_score: 0,
        match_quality: 'Manual',
        city: org.city,
        state: org.state,
        customer_status: org.customer_status
      }]);
    }
    // Clear search
    setCboSearchQuery('');
    setCboSearchResults([]);
  };

  // ROI Modeler handlers
  const handleROIConfigChange = (newConfig: ROIConfigData) => {
    setRoiConfig(newConfig);
    // Persist to localStorage for recovery
    if (createdProgramId) {
      const storageKey = `roi_config_program_${createdProgramId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        config: newConfig,
        scenarios: roiScenarios,
        dataSources: connectedDataSources,
        selectedScenarioId,
        updatedAt: new Date().toISOString()
      }));
    }
  };

  const handleScenarioSelect = (scenario: any) => {
    setSelectedScenarioId(scenario.id);
    // Also update the config to match the selected scenario
    if (scenario.config) {
      setRoiConfig(scenario.config);
    }
  };

  const handleScenariosChange = (scenarios: any[]) => {
    setRoiScenarios(scenarios);
    // Persist to localStorage
    if (createdProgramId) {
      const storageKey = `roi_config_program_${createdProgramId}`;
      const existing = localStorage.getItem(storageKey);
      const data = existing ? JSON.parse(existing) : {};
      data.scenarios = scenarios;
      data.updatedAt = new Date().toISOString();
      localStorage.setItem(storageKey, JSON.stringify(data));
    }
  };

  const handleDataSourcesChange = (sources: string[]) => {
    // Convert string IDs to ConnectedDataSource objects
    const dataSourceObjects: ConnectedDataSource[] = sources.map(id => ({
      id,
      name: id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      type: id.includes('ai') ? 'ai' : id.includes('cms') || id.includes('state') || id.includes('census') ? 'public' : 'internal',
      lastSync: new Date().toISOString()
    }));
    setConnectedDataSources(dataSourceObjects);
    // Persist to localStorage
    if (createdProgramId) {
      const storageKey = `roi_config_program_${createdProgramId}`;
      const existing = localStorage.getItem(storageKey);
      const data = existing ? JSON.parse(existing) : {};
      data.dataSources = dataSourceObjects;
      data.updatedAt = new Date().toISOString();
      localStorage.setItem(storageKey, JSON.stringify(data));
    }
  };

  // Load persisted ROI data when entering step 7
  const loadPersistedROIData = () => {
    if (!createdProgramId) return;

    const storageKey = `roi_config_program_${createdProgramId}`;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.config) setRoiConfig(data.config);
        if (data.scenarios) setRoiScenarios(data.scenarios);
        if (data.dataSources) setConnectedDataSources(data.dataSources);
        if (data.selectedScenarioId) setSelectedScenarioId(data.selectedScenarioId);
        console.log('✅ Loaded persisted ROI data for program:', createdProgramId);
      } catch (err) {
        console.error('Error loading persisted ROI data:', err);
      }
    }
  };

  // Load ROI data when entering step 7
  useEffect(() => {
    if (currentStep === 7) {
      loadPersistedROIData();
    }
  }, [currentStep, createdProgramId]);

  const saveROIConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      // Prepare comprehensive ROI data including scenarios and data sources
      const roiPayload = {
        ...roiConfig,
        scenarios: roiScenarios.length > 0 ? roiScenarios : undefined,
        connected_data_sources: connectedDataSources.length > 0 ? connectedDataSources : undefined,
        selected_scenario_id: selectedScenarioId
      };

      const response = await fetch(
        getWeaveinApiUrl(`/api/roi/programs/${createdProgramId}/model-data`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify(roiPayload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save ROI configuration');
      }

      // Clear localStorage cache after successful save
      if (createdProgramId) {
        const storageKey = `roi_config_program_${createdProgramId}`;
        localStorage.removeItem(storageKey);
        console.log('✅ Saved ROI config and cleared cache for program:', createdProgramId);
      }

      // Move to review step
      setCurrentStep(8);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save ROI config');
    } finally {
      setLoading(false);
    }
  };

  const saveMilestoneConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      if (milestoneConfigs.length === 0) {
        // No milestones configured - just move to ROI
        setCurrentStep(7);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      // Send all milestones in a single request
      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${createdProgramId}/milestones`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify(milestoneConfigs.map(config => ({
            milestone_id: config.milestone_id,
            milestone_name: config.milestone_name,
            milestone_type: config.milestone_type,
            is_required: config.is_required,
            target_quantity: config.target_quantity,
            cbo_payout_amount: config.cbo_payout_amount,
            member_payout_amount: config.member_payout_amount,
            points_awarded: config.points_awarded
          })))
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save milestone configuration');
      }

      // Move to ROI step
      setCurrentStep(7);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save milestones');
    } finally {
      setLoading(false);
    }
  };

  const addMilestoneConfig = () => {
    const newConfig: MilestoneConfigItem = {
      milestone_id: 0,
      milestone_name: '',
      milestone_type: '',
      is_required: false,
      target_quantity: 1,
      cbo_payout_amount: 0,
      member_payout_amount: 0,
      points_awarded: 0
    };
    setMilestoneConfigs([...milestoneConfigs, newConfig]);
  };

  const updateMilestoneConfig = (index: number, field: keyof MilestoneConfigItem, value: any) => {
    const updated = [...milestoneConfigs];
    if (field === 'milestone_id') {
      const selected = availableMilestones.find(m => m.id === parseInt(value));
      if (selected) {
        updated[index] = {
          ...updated[index],
          milestone_id: selected.id,
          milestone_name: selected.milestone_name,
          milestone_type: selected.activity_type,
          points_awarded: selected.points_awarded,
          member_payout_amount: selected.monetary_value
        };
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setMilestoneConfigs(updated);
  };

  const removeMilestoneConfig = (index: number) => {
    setMilestoneConfigs(milestoneConfigs.filter((_, i) => i !== index));
  };

  const updateProgram = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${createdProgramId}`),
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify({
            name: programData.name,
            program_type: programData.program_type,
            description: programData.description,
            eligibility_criteria: {
              criteria: programData.eligibility_criteria,
              target_population: programData.target_population,
              geographic_scope: programData.geographic_scope
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update program');
      }

      console.log(`✅ Updated draft program: ${programData.name}`);
      setCurrentStep(currentStep + 1);
    } catch (err) {
      console.error('Error updating program:', err);
      setError(err instanceof Error ? err.message : 'Failed to update program');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createProgram = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use shared programsAPI service for consistent program creation
      const program = await programsAPI.createProgram({
        name: programData.name,
        program_type: programData.program_type,
        description: programData.description,
        is_active: false,
        // Store eligibility_criteria as object (backend expects dict)
        eligibility_criteria: {
          criteria: programData.eligibility_criteria,
          target_population: programData.target_population,
          geographic_scope: programData.geographic_scope
        }
      });

      setCreatedProgramId(program.id);
      setCurrentStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create program');
    } finally {
      setLoading(false);
    }
  };

  const saveMatchingCriteria = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl('/api/program-matching-criteria'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify({
            program_id: createdProgramId,
            sponsor_id: sponsorId,
            ...matchingCriteria
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to save matching criteria';
        throw new Error(errorMessage);
      }

      setCurrentStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save criteria');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchedCBOs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const response = await fetch(
        getWeaveinApiUrl(`/api/sponsors/${sponsorId}/programs/${createdProgramId}/recommended-cbos?min_match_score=${matchingCriteria.min_match_score}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch matched CBOs');
      }

      const cbos = await response.json();
      setMatchedCBOs(cbos);
      setCurrentStep(4);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  const saveTerms = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      // First check if terms already exist for this program
      const checkResponse = await fetch(
        getWeaveinApiUrl(`/api/sponsors/${sponsorId}/terms?program_id=${createdProgramId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || ''
          }
        }
      );

      let existingTermsId: number | null = null;
      if (checkResponse.ok) {
        const existingTerms = await checkResponse.json();
        // Find terms for this specific program
        const programTerms = existingTerms.find((t: any) => t.program_id === createdProgramId);
        if (programTerms) {
          existingTermsId = programTerms.id;
        }
      }

      // Map wizard terms to backend schema
      const termsPayload = {
        program_id: createdProgramId,
        terms_name: `Incentive Terms - ${programData.name}`,
        terms_description: `Payment terms for ${programData.name}`,
        base_enrollment_incentive: termsData.enrollment_payment || 0,
        per_member_rate: termsData.enrollment_payment || 0,
        milestone_bonus_structure: termsData.milestone_payments.length > 0
          ? { milestones: termsData.milestone_payments }
          : null,
        payment_schedule: 'MONTHLY',
        payment_method: 'DIRECT_DEPOSIT',
        payment_terms_days: 30,
        minimum_payout_threshold: 0,
        terms_version: '1.0',
        auto_renew: false
      };

      // Use PUT to update if terms exist, POST to create new
      const url = existingTermsId
        ? getWeaveinApiUrl(`/api/sponsors/${sponsorId}/terms/${existingTermsId}`)
        : getWeaveinApiUrl(`/api/sponsors/${sponsorId}/terms`);

      const method = existingTermsId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CBO-ID': cboId || ''
        },
        body: JSON.stringify(termsPayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to save terms';
        throw new Error(errorMessage);
      }

      // Fetch available milestones for next step
      await fetchAvailableMilestones();
      setCurrentStep(6);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save terms');
    } finally {
      setLoading(false);
    }
  };

  const launchProgram = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const activeCboId = localStorage.getItem('active_cbo_id');

      // Update program to active
      await fetch(getWeaveinApiUrl(`/api/programs/${createdProgramId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CBO-ID': activeCboId || ''
        },
        body: JSON.stringify({ is_active: true })
      });

      // Invite selected CBOs
      for (const cboId of selectedCBOs) {
        await fetch(getWeaveinApiUrl('/api/sponsor-invitations'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': activeCboId || ''
          },
          body: JSON.stringify({
            sponsor_id: sponsorId,
            program_id: createdProgramId,
            cbo_id: cboId,
            invitation_message: `You're invited to join ${programData.name}!`
          })
        });
      }

      // Clear the draft after successful launch
      clearWizardDraft();

      if (onComplete && createdProgramId) {
        onComplete(createdProgramId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to launch program');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="name">{t('sponsors.program_name', 'Program Name *')}</Label>
              <Input
                id="name"
                value={programData.name}
                onChange={(e) => setProgramData({ ...programData, name: e.target.value })}
                placeholder="e.g., CalAIM Enhanced Care Management"
              />
            </div>

            <div>
              <Label>{t('sponsors.program_type', 'Program Type *')}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {programTypes.map((type) => (
                  <Card
                    key={type.value}
                    className={`p-4 cursor-pointer transition-all ${
                      programData.program_type === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'hover:border-gray-400'
                    }`}
                    onClick={() => setProgramData({ ...programData, program_type: type.value })}
                  >
                    <div className="font-semibold text-sm">{type.label}</div>
                    <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="description">{t('sponsors.description', 'Description *')}</Label>
              <Textarea
                id="description"
                value={programData.description}
                onChange={(e) => setProgramData({ ...programData, description: e.target.value })}
                rows={4}
                placeholder="Describe the program goals and objectives..."
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="eligibility">{t('sponsors.eligibility_criteria', 'Eligibility Criteria')}</Label>
              <Textarea
                id="eligibility"
                value={programData.eligibility_criteria}
                onChange={(e) => setProgramData({ ...programData, eligibility_criteria: e.target.value })}
                rows={3}
                placeholder="Who is eligible for this program?"
              />
            </div>

            <div>
              <Label htmlFor="target">{t('sponsors.target_population', 'Target Population')}</Label>
              <Textarea
                id="target"
                value={programData.target_population}
                onChange={(e) => setProgramData({ ...programData, target_population: e.target.value })}
                rows={3}
                placeholder="Describe the target population..."
              />
            </div>

            <div>
              <Label htmlFor="geography">{t('sponsors.geographic_scope', 'Geographic Scope')}</Label>
              <Input
                id="geography"
                value={programData.geographic_scope}
                onChange={(e) => setProgramData({ ...programData, geographic_scope: e.target.value })}
                placeholder="e.g., Los Angeles County, California"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label>Required Services (select multiple)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {SERVICE_OPTIONS.map((service) => (
                  <label key={service} className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={matchingCriteria.required_services.includes(service)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...matchingCriteria.required_services, service]
                          : matchingCriteria.required_services.filter((s) => s !== service);
                        setMatchingCriteria({ ...matchingCriteria, required_services: updated });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{service.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>{t('sponsors.target_sdoh_areas', 'Target SDOH Areas')}</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {SDOH_OPTIONS.map((sdoh) => (
                  <label key={sdoh} className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={matchingCriteria.target_sdoh_areas.includes(sdoh)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...matchingCriteria.target_sdoh_areas, sdoh]
                          : matchingCriteria.target_sdoh_areas.filter((s) => s !== sdoh);
                        setMatchingCriteria({ ...matchingCriteria, target_sdoh_areas: updated });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{sdoh.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min_score">Minimum Match Score (%)</Label>
                <Input
                  id="min_score"
                  type="number"
                  value={matchingCriteria.min_match_score}
                  onChange={(e) => setMatchingCriteria({ ...matchingCriteria, min_match_score: parseInt(e.target.value) })}
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <Label htmlFor="max_cbos">{t('sponsors.maximum_cbos', 'Maximum CBOs')}</Label>
                <Input
                  id="max_cbos"
                  type="number"
                  value={matchingCriteria.max_cbos_total}
                  onChange={(e) => setMatchingCriteria({ ...matchingCriteria, max_cbos_total: parseInt(e.target.value) })}
                  min={1}
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">{t('sponsors.select_cbos_to_invite', 'Select CBOs to Invite')}</h3>
                <p className="text-sm text-gray-600">
                  {t('sponsors.choose_from_matched_cbos_or', 'Choose from matched CBOs or manually add organizations.')}
                </p>
              </div>
              <Badge>{selectedCBOs.length} selected</Badge>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  cboSelectionTab === 'matched'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setCboSelectionTab('matched')}
              >
                <Users className="w-4 h-4 inline mr-2" />
                Matched CBOs ({matchedCBOs.filter(c => c.match_quality !== 'Manual').length})
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  cboSelectionTab === 'manual'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setCboSelectionTab('manual')}
              >
                <Plus className="w-4 h-4 inline mr-2" />
                {t('sponsors.manual_addition', 'Manual Addition')}
              </button>
            </div>

            {/* Matched CBOs Tab */}
            {cboSelectionTab === 'matched' && (
              <>
                {matchedCBOs.filter(c => c.match_quality !== 'Manual').length === 0 ? (
                  <Card className="p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600">{t('sponsors.no_cbos_match_your_criteria', 'No CBOs match your criteria yet.')}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {t('sponsors.try_adjusting_matching_criteria_or', 'Try adjusting matching criteria or use Manual Addition to search for CBOs.')}
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {matchedCBOs.filter(c => c.match_quality !== 'Manual').map((cbo) => (
                      <Card
                        key={cbo.cbo_id}
                        className={`p-4 cursor-pointer transition-all ${
                          selectedCBOs.includes(cbo.cbo_id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'hover:border-gray-400'
                        }`}
                        onClick={() => {
                          setSelectedCBOs(
                            selectedCBOs.includes(cbo.cbo_id)
                              ? selectedCBOs.filter((id) => id !== cbo.cbo_id)
                              : [...selectedCBOs, cbo.cbo_id]
                          );
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{cbo.cbo_name}</h4>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                              <span>Match: {Number(cbo.match_score).toFixed(0)}%</span>
                              <span>Quality: {cbo.match_quality}</span>
                            </div>
                          </div>
                          {selectedCBOs.includes(cbo.cbo_id) && (
                            <CheckCircle2 className="w-6 h-6 text-blue-600" />
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Manual Addition Tab */}
            {cboSelectionTab === 'manual' && (
              <div className="space-y-4">
                {/* Search Existing Organizations */}
                <div>
                  <Label>{t('sponsors.search_existing_organizations', 'Search Existing Organizations')}</Label>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={cboSearchQuery}
                      onChange={(e) => {
                        setCboSearchQuery(e.target.value);
                        searchCBOs(e.target.value);
                      }}
                      placeholder={t('sponsors.search_by_name_npi_city', 'Search by name, NPI, city, or zip code...')}
                      className="pl-10"
                    />
                  </div>

                  {/* Search Results */}
                  {cboSearchResults.length > 0 && (
                    <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                      {cboSearchResults.map((org) => (
                        <div
                          key={org.id}
                          className="p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                          onClick={() => addCboFromSearch(org)}
                        >
                          <div>
                            <p className="font-medium text-sm">{org.name}</p>
                            <p className="text-xs text-gray-500">
                              {org.city}, {org.state} {org.zip_code}
                              {org.customer_status && (
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                  org.customer_status === 'CUSTOMER' ? 'bg-green-100 text-green-800' :
                                  org.customer_status === 'OPPORTUNITY' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {org.customer_status}
                                </span>
                              )}
                            </p>
                          </div>
                          <Plus className="w-4 h-4 text-blue-600" />
                        </div>
                      ))}
                    </div>
                  )}

                  {searchingCbos && (
                    <p className="text-sm text-gray-500 mt-2">{t('sponsors.searching', 'Searching...')}</p>
                  )}
                </div>

                {/* Create New Organization */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{t('sponsors.create_new_organization', 'Create New Organization')}</h4>
                      <p className="text-sm text-gray-500">
                        {t('sponsors.add_a_cbo_that_doesnt', 'Add a CBO that doesn\'t exist in the system yet')}
                      </p>
                    </div>
                    {!showNewCboForm && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewCboForm(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('sponsors.new_cbo', 'New CBO')}
                      </Button>
                    )}
                  </div>

                  {showNewCboForm && (
                    <Card className="p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-sm">{t('sponsors.new_opportunity_cbo', 'New Opportunity CBO')}</h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowNewCboForm(false)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <Label>{t('sponsors.organization_name', 'Organization Name *')}</Label>
                          <Input
                            value={newCboData.name}
                            onChange={(e) => setNewCboData({ ...newCboData, name: e.target.value })}
                            placeholder={t('sponsors.organization_name_1', 'Organization name')}
                          />
                        </div>
                        <div>
                          <Label>{t('sponsors.city', 'City')}</Label>
                          <Input
                            value={newCboData.city}
                            onChange={(e) => setNewCboData({ ...newCboData, city: e.target.value })}
                            placeholder={t('sponsors.city_1', 'City')}
                          />
                        </div>
                        <div>
                          <Label>{t('sponsors.state', 'State')}</Label>
                          <Input
                            value={newCboData.state}
                            onChange={(e) => setNewCboData({ ...newCboData, state: e.target.value })}
                            placeholder={t('sponsors.state_1', 'State')}
                          />
                        </div>
                        <div>
                          <Label>{t('sponsors.county', 'County')}</Label>
                          <Input
                            value={newCboData.county}
                            onChange={(e) => setNewCboData({ ...newCboData, county: e.target.value })}
                            placeholder={t('sponsors.county_1', 'County')}
                          />
                        </div>
                        <div>
                          <Label>{t('sponsors.zip_code', 'Zip Code')}</Label>
                          <Input
                            value={newCboData.zip_code}
                            onChange={(e) => setNewCboData({ ...newCboData, zip_code: e.target.value })}
                            placeholder={t('sponsors.zip_code_1', 'Zip code')}
                          />
                        </div>
                        <div>
                          <Label>{t('sponsors.contact_name', 'Contact Name')}</Label>
                          <Input
                            value={newCboData.contact_name}
                            onChange={(e) => setNewCboData({ ...newCboData, contact_name: e.target.value })}
                            placeholder={t('sponsors.contact_name_1', 'Contact name')}
                          />
                        </div>
                        <div>
                          <Label>{t('sponsors.contact_email', 'Contact Email')}</Label>
                          <Input
                            value={newCboData.contact_email}
                            onChange={(e) => setNewCboData({ ...newCboData, contact_email: e.target.value })}
                            placeholder={t('sponsors.contact_email_1', 'Contact email')}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>{t('sponsors.notes', 'Notes')}</Label>
                          <Textarea
                            value={newCboData.notes}
                            onChange={(e) => setNewCboData({ ...newCboData, notes: e.target.value })}
                            placeholder={t('sponsors.notes_about_this_organization', 'Notes about this organization...')}
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end">
                        <Button
                          onClick={createOpportunityCBO}
                          disabled={loading || !newCboData.name}
                        >
                          {loading ? 'Creating...' : 'Create & Add to Selection'}
                        </Button>
                      </div>
                    </Card>
                  )}
                </div>

                {/* Manually Added CBOs */}
                {matchedCBOs.filter(c => c.match_quality === 'Manual').length > 0 && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold text-sm mb-3">{t('sponsors.manually_added_cbos', 'Manually Added CBOs')}</h4>
                    <div className="space-y-2">
                      {matchedCBOs.filter(c => c.match_quality === 'Manual').map((cbo) => (
                        <Card
                          key={cbo.cbo_id}
                          className={`p-3 cursor-pointer transition-all ${
                            selectedCBOs.includes(cbo.cbo_id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'hover:border-gray-400'
                          }`}
                          onClick={() => {
                            setSelectedCBOs(
                              selectedCBOs.includes(cbo.cbo_id)
                                ? selectedCBOs.filter((id) => id !== cbo.cbo_id)
                                : [...selectedCBOs, cbo.cbo_id]
                            );
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-sm">{cbo.cbo_name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">
                                  {cbo.city && cbo.state ? `${cbo.city}, ${cbo.state}` : 'Manual'}
                                </span>
                                {cbo.customer_status && (
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    cbo.customer_status === 'OPPORTUNITY' ? 'bg-blue-100 text-blue-800' :
                                    cbo.customer_status === 'CUSTOMER' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {cbo.customer_status}
                                  </span>
                                )}
                              </div>
                            </div>
                            {selectedCBOs.includes(cbo.cbo_id) && (
                              <CheckCircle2 className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            {/* Quick Setup Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('sponsors.quick_terms_setup', 'Quick Terms Setup')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="enrollment">Enrollment Payment ($)</Label>
                  <Input
                    id="enrollment"
                    type="number"
                    value={termsData.enrollment_payment}
                    onChange={(e) => setTermsData({ ...termsData, enrollment_payment: parseFloat(e.target.value) })}
                    min={0}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('sponsors.payment_per_member_enrolled', 'Payment per member enrolled')}</p>
                </div>
                <div>
                  <Label htmlFor="completion">{t('sponsors.completion_bonus', 'Completion Bonus ($)')}</Label>
                  <Input
                    id="completion"
                    type="number"
                    value={termsData.completion_bonus}
                    onChange={(e) => setTermsData({ ...termsData, completion_bonus: parseFloat(e.target.value) })}
                    min={0}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('sponsors.bonus_for_program_completion', 'Bonus for program completion')}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 mt-4">
                <input
                  type="checkbox"
                  id="outcome_based"
                  checked={termsData.outcome_based_payment}
                  onChange={(e) => setTermsData({ ...termsData, outcome_based_payment: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="outcome_based" className="cursor-pointer">
                  {t('sponsors.enable_outcomebased_payments', 'Enable outcome-based payments')}
                </Label>
              </div>
            </div>

            {/* Advanced Options Info */}
            <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-start space-x-3">
                <Award className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">{t('sponsors.advanced_configuration_available', 'Advanced Configuration Available')}</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    {t('sponsors.this_quick_setup_creates_basic', 'This quick setup creates basic terms. After launching, you can configure advanced features:')}
                  </p>
                  <ul className="text-sm text-blue-800 space-y-1 mb-4">
                    <li>• Performance tiers with incentive multipliers</li>
                    <li>• Milestone bonuses and configurable payouts</li>
                    <li>• Custom payment schedules and methods</li>
                    <li>• Compliance requirements and reporting</li>
                    <li>• Provider and member incentive terms</li>
                  </ul>
                  <p className="text-sm text-blue-700 bg-blue-100 p-3 rounded-md">
                    <strong>{t('sponsors.access_advanced_options_after_launch', 'Access advanced options after launch:')}</strong><br />
                    • <strong>{t('sponsors.advanced_terms', 'Advanced Terms:')}</strong> {t('sponsors.sponsor_dashboard_financial_tab_manage', 'Sponsor Dashboard → Financial tab → Manage Terms')}<br />
                    • <strong>{t('sponsors.milestones', 'Milestones:')}</strong> {t('sponsors.sponsor_dashboard_goals_tab_configure', 'Sponsor Dashboard → Goals tab → Configure Milestones')}<br />
                    • <strong>{t('sponsors.edit_program', 'Edit Program:')}</strong> {t('sponsors.sponsor_dashboard_overview_tab_select', 'Sponsor Dashboard → Overview tab → Select Program')}
                  </p>
                </div>
              </div>
            </Card>

            {/* Note */}
            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              💡 <strong>{t('sponsors.tip', 'Tip:')}</strong> {t('sponsors.you_can_modify_terms_anytime', 'You can modify terms anytime after program launch via the Sponsor Dashboard → Financial tab.')}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <ROIModeler
              programId={createdProgramId || undefined}
              programType={programData.program_type}
              sponsorId={sponsorId}
              onConfigChange={handleROIConfigChange}
              onScenarioSelect={handleScenarioSelect}
              onScenariosChange={handleScenariosChange}
              onDataSourcesChange={handleDataSourcesChange}
              initialConfig={roiConfig}
              initialScenarios={roiScenarios as any}
              initialConnectedSources={connectedDataSources.map(ds => ds.id)}
            />
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('sponsors.configure_program_milestones', 'Configure Program Milestones')}</h3>
                <p className="text-sm text-gray-600">{t('sponsors.select_milestones_and_configure_payouts', 'Select milestones and configure payouts for this program')}</p>
              </div>
              <Button onClick={addMilestoneConfig} variant="outline" className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                {t('sponsors.add_milestone', 'Add Milestone')}
              </Button>
            </div>

            {milestoneConfigs.length === 0 ? (
              <Card className="p-8 text-center">
                <Award className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">{t('sponsors.no_milestones_configured_yet', 'No milestones configured yet')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('sponsors.click_add_milestone_to_add', 'Click "Add Milestone" to add performance milestones')}</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {milestoneConfigs.map((config, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="font-semibold">Milestone {index + 1}</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeMilestoneConfig(index)}
                        className="text-red-600"
                      >
                        {t('sponsors.remove', 'Remove')}
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="col-span-2">
                        <Label>{t('sponsors.milestone_type', 'Milestone Type')}</Label>
                        <select
                          value={config.milestone_id}
                          onChange={(e) => updateMilestoneConfig(index, 'milestone_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={0}>{t('sponsors.select_a_milestone', 'Select a milestone...')}</option>
                          {availableMilestones.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.milestone_name} ({m.activity_type.replace(/_/g, ' ')})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>{t('sponsors.target_quantity', 'Target Quantity')}</Label>
                        <Input
                          type="number"
                          value={config.target_quantity}
                          onChange={(e) => updateMilestoneConfig(index, 'target_quantity', parseInt(e.target.value) || 1)}
                          min={1}
                        />
                      </div>
                      <div>
                        <Label>{t('sponsors.cbo_payout', 'CBO Payout ($)')}</Label>
                        <Input
                          type="number"
                          value={config.cbo_payout_amount}
                          onChange={(e) => updateMilestoneConfig(index, 'cbo_payout_amount', parseFloat(e.target.value) || 0)}
                          min={0}
                        />
                      </div>
                      <div>
                        <Label>{t('sponsors.member_payout', 'Member Payout ($)')}</Label>
                        <Input
                          type="number"
                          value={config.member_payout_amount}
                          onChange={(e) => updateMilestoneConfig(index, 'member_payout_amount', parseFloat(e.target.value) || 0)}
                          min={0}
                        />
                      </div>
                    </div>

                    <div className="flex items-center mt-3">
                      <input
                        type="checkbox"
                        id={`required-${index}`}
                        checked={config.is_required}
                        onChange={(e) => updateMilestoneConfig(index, 'is_required', e.target.checked)}
                        className="rounded mr-2"
                      />
                      <Label htmlFor={`required-${index}`} className="cursor-pointer text-sm">
                        {t('sponsors.required_for_program_completion', 'Required for program completion')}
                      </Label>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {availableMilestones.length === 0 && (
              <div className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                <strong>{t('sponsors.note', 'Note:')}</strong> {t('sponsors.no_milestone_types_available_create', 'No milestone types available. Create milestone types in the Milestone Management page first.')}
              </div>
            )}
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-xl font-bold mb-4">{t('sponsors.program_summary', 'Program Summary')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">{t('sponsors.program_name_1', 'Program Name')}</p>
                  <p className="font-semibold">{programData.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('sponsors.program_type_1', 'Program Type')}</p>
                  <p className="font-semibold">{programData.program_type.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('sponsors.matched_cbos', 'Matched CBOs')}</p>
                  <p className="font-semibold">{matchedCBOs.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('sponsors.cbos_to_invite', 'CBOs to Invite')}</p>
                  <p className="font-semibold">{selectedCBOs.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('sponsors.enrollment_payment', 'Enrollment Payment')}</p>
                  <p className="font-semibold">${termsData.enrollment_payment}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('sponsors.completion_bonus_1', 'Completion Bonus')}</p>
                  <p className="font-semibold">${termsData.completion_bonus}</p>
                </div>
              </div>
            </Card>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-900">{t('sponsors.ready_to_launch', 'Ready to Launch')}</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Clicking "Launch Program" will activate the program and send invitations to {selectedCBOs.length} selected CBOs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Format saved date for display
  const formatSavedDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Show draft prompt if we have a saved draft
  if (showDraftPrompt && draftPrograms.length > 0) {
    const draft = draftPrograms[0] as WizardDraftState;
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('sponsors.resume_your_draft', 'Resume Your Draft?')}</h2>
            <p className="text-gray-600">
              {t('sponsors.you_have_an_unsaved_program', 'You have an unsaved program draft from your previous session.')}
            </p>
          </div>

          <Card className="p-4 bg-gray-50 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('sponsors.program_name_2', 'Program Name')}</span>
                <span className="text-sm font-medium">{draft.programData.name || 'Untitled'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('sponsors.program_type_2', 'Program Type')}</span>
                <span className="text-sm font-medium">{draft.programData.program_type?.replace(/_/g, ' ') || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('sponsors.progress', 'Progress')}</span>
                <span className="text-sm font-medium">Step {draft.currentStep} of {WIZARD_STEPS.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('sponsors.last_saved', 'Last Saved')}</span>
                <span className="text-sm font-medium">{formatSavedDate(draft.savedAt)}</span>
              </div>
            </div>
          </Card>

          <div className="flex flex-col gap-3">
            <Button
              onClick={() => resumeDraft(draft)}
              className="w-full bg-v2accent hover:bg-v2accent/90"
            >
              <ChevronRight className="w-4 h-4 mr-2" />
              {t('sponsors.resume_draft', 'Resume Draft')}
            </Button>
            <Button
              variant="outline"
              onClick={discardDraft}
              className="w-full"
            >
              {t('sponsors.start_fresh', 'Start Fresh')}
            </Button>
            {onCancel && (
              <Button
                variant="outline"
                onClick={onCancel}
                className="w-full text-gray-500"
              >
                {t('sponsors.cancel', 'Cancel')}
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('sponsors.program_setup_wizard', 'Program Setup Wizard')}</h1>
        <p className="text-gray-600">{t('sponsors.create_and_configure_your_program', 'Create and configure your program in 8 easy steps')}</p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Step {currentStep} of {WIZARD_STEPS.length}
          </span>
          <span className="text-sm text-gray-600">{Math.round(progressPercentage)}% complete</span>
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </div>

      {/* Step Indicators */}
      <div className="flex items-center justify-between mb-8">
        {WIZARD_STEPS.map((step, index) => {
          const StepIcon = step.icon;
          const isCompleted = step.id < currentStep;
          const isCurrent = step.id === currentStep;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : <StepIcon className="w-6 h-6" />}
                </div>
                <p className={`text-xs font-medium text-center ${isCurrent ? 'text-blue-600' : 'text-gray-600'}`}>
                  {step.name}
                </p>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Error Alert */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">{t('sponsors.error', 'Error')}</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
            {onCancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={onCancel}
                className="text-red-600 hover:bg-red-100"
              >
                {t('sponsors.close', 'Close')}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Step Content */}
      <Card className="p-8 mb-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {WIZARD_STEPS[currentStep - 1].name}
          </h2>
          <p className="text-gray-600">{WIZARD_STEPS[currentStep - 1].description}</p>
          {WIZARD_STEPS[currentStep - 1].guidance && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">💡 Guidance:</span> {WIZARD_STEPS[currentStep - 1].guidance}
              </p>
            </div>
          )}
        </div>

        {renderStepContent()}
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="text-gray-600"
          >
            {t('sponsors.cancel_1', 'Cancel')}
          </Button>
          {currentStep > 1 && (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={loading}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t('sponsors.back', 'Back')}
            </Button>
          )}
        </div>

        <Button onClick={handleNext} disabled={loading || (currentStep === 1 && !programData.name)}>
          {loading ? (
            'Processing...'
          ) : currentStep === WIZARD_STEPS.length ? (
            <>
              <Send className="w-4 h-4 mr-2" />
              {t('sponsors.launch_program', 'Launch Program')}
            </>
          ) : (
            <>
              {t('sponsors.continue', 'Continue')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProgramSetupWizard;
